// react
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

// components
import { Button } from '@/components/atoms';

// glidejs
import Glide from '@glidejs/glide';
import '@glidejs/glide/dist/css/glide.core.min.css';

interface CarouselResponsiveProps {
  className?: string;
  items: React.ReactNode[];
  perView?: number;
}

/**
 * CarouselResponsive shows a responsive carousel.
 */
export const CarouselResponsive = ({
  className,
  items = [],
  perView = 1,
}: CarouselResponsiveProps) => {
  // state
  const [gliderRef, setGliderRef] = useState<HTMLDivElement | null>(null);
  const [index, setIndex] = useState(0);

  const handleRef = useCallback((node: HTMLDivElement) => {
    if (node) setGliderRef(node);
  }, []);

  useEffect(() => {
    if (!gliderRef) return;
    const glide = new Glide(gliderRef, {
      peek: 0,
      perView,
    });

    glide.on('run', () => {
      setIndex(glide.index);
    });

    glide.mount();

    return () => {
      glide.destroy();
    };
  }, [gliderRef, perView]);

  return (
    <Wrapper className={className} id='glide' ref={handleRef}>
      <GlideTrack className='glide__track' data-glide-el='track'>
        <GlideSlides className='glide__slides'>
          {items.map((item) => (
            <Slide className='glide__slide'>{item}</Slide>
          ))}
        </GlideSlides>
      </GlideTrack>
      <Actions className='glide__arrows' data-glide-el='controls'>
        <Button
          className='glide__arrow glide__arrow--left'
          data-glide-dir='<'
          ariaLabel='carousel left'
          iconLeft='chevron-left'
          variant='link'
          size='lg'
          isDisabled={index === 0}
        />
        <Button
          className='glide__arrow glide__arrow--right'
          data-glide-dir='>'
          ariaLabel='carousel right'
          iconLeft='chevron-right'
          variant='link'
          size='lg'
          isDisabled={index === items.length - 1}
        />
      </Actions>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 0 ${(props) => props.theme.spacing[24]};
  overflow: visible;
`;

const GlideTrack = styled.div`
  overflow: visible;
`;

const GlideSlides = styled.ul`
  overflow: visible;
`;

const Slide = styled.li`
  display: flex;
  justify-content: center;
  overflow: visible;
`;

const Actions = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
`;

CarouselResponsive.displayName = 'CarouselResponsive';

export default CarouselResponsive;
